import React, { useEffect, useMemo, useState } from 'react';
import './HousingManage.scss';

interface HousingMember {
  actorId: number;
  name: string;
}

interface HouseChest {
  refrId: number;
  id: string;
  name: string;
  allowedActorIds: number[];
  // Lowest hold-faction rank id allowed in, null when no rank rule is set.
  rankAccess?: number | null;
  // The commerce's cash box: the weekly rent is taken from the gold in it.
  isCash?: boolean;
}

// Present for a commerce only (a shop rented from a hold), null for a house.
interface CommerceState {
  rentPricePerWeek: number;
  cashChestRefrId: number | null;
  // Gold in the cash box, null when none is set or it cannot be read right now.
  cashBalance: number | null;
}

interface OwnedHouse {
  refrId: number;
  displayName: string;
  members: HousingMember[];
  // Commerce: people who run it with the renter (same rights, only the renter picks them)
  coOwners?: HousingMember[];
  canManageCoOwners?: boolean;
  maxMembers: number;
  isLocked: boolean;
  // false when this payload came from a Steward/admin managing a hold house
  // they don't live in (see housingSystem.ts's sendChestView): keys, journal
  // and sale are not theirs, only the chests are.
  isOwner: boolean;
  // True for a purchased (not rented) house outside any hold.
  canSell?: boolean;
  chests: HouseChest[];
  // Ranks of the hold faction, empty for a free house (no rank-based access).
  holdRanks?: { id: number; name: string }[];
  maxChests: number;
  commerce?: CommerceState | null;
  // Newest first, occupant-only (absent when a Steward/admin views the house).
  journal?: { at: number; text: string }[];
}

type Tab = 'keys' | 'chests' | 'journal' | 'sale';

const formatJournalDate = (ts: number): string => {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'housing-manage', ...payload }));
  }
};

// Two different jobs share this panel and used to be stacked into one long
// scroll: the occupant looking after their own homes (keys, chests, journal,
// sale), and a Jarl/Intendant looking at one house of their hold (chests only).
// Here the occupant gets a house selector and four tabs, the steward gets a
// clearly labelled single-purpose view. It is docked to the right edge so the
// door or the room you are looking at stays visible.
const HousingManage: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [houses, setHouses] = useState<OwnedHouse[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [tab, setTab] = useState<Tab>('keys');
  const [nameDraft, setNameDraft] = useState('');
  const [coOwnerDraft, setCoOwnerDraft] = useState('');
  const [saleBuyer, setSaleBuyer] = useState('');
  const [salePrice, setSalePrice] = useState('');
  // Keyed by chest refrId - an in-progress rename, only shown while editing.
  const [renameDrafts, setRenameDrafts] = useState<Record<number, string>>({});
  // Which chest (by refrId) currently has its access settings expanded.
  const [expandedChest, setExpandedChest] = useState<number | null>(null);

  useEffect(() => {
    const onState = (e: Event) => {
      const detail = (e as CustomEvent).detail as { houses: OwnedHouse[] };
      const list = Array.isArray(detail.houses) ? detail.houses : [];
      setHouses(list);
      setSelected((prev) => (prev !== null && list.some((h) => h.refrId === prev) ? prev : list[0]?.refrId ?? null));
      // A steward opened to look at chests; an occupant starts on the keys.
      setTab((prev) => (list[0] && !list[0].isOwner ? 'chests' : prev));
      setVisible(true);
    };
    const onHide = () => setVisible(false);
    window.addEventListener('skymp5-client:housingManageState', onState);
    window.addEventListener('skymp5-client:hideHousingManage', onHide);
    return () => {
      window.removeEventListener('skymp5-client:housingManageState', onState);
      window.removeEventListener('skymp5-client:hideHousingManage', onHide);
    };
  }, []);

  const house = useMemo(() => houses.find((h) => h.refrId === selected) ?? houses[0] ?? null, [houses, selected]);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  const chooseHouse = (refrId: number) => {
    setSelected(refrId);
    setNameDraft('');
    setExpandedChest(null);
    setRenameDrafts({});
  };

  const patchHouse = (refrId: number, patch: (h: OwnedHouse) => OwnedHouse) =>
    setHouses((prev) => prev.map((h) => (h.refrId === refrId ? patch(h) : h)));

  const addByName = (refrId: number) => {
    const name = nameDraft.trim();
    if (!name) return;
    send({ action: 'addByName', refrId, name });
    setNameDraft('');
  };

  const addCoOwnerByName = (refrId: number) => {
    const name = coOwnerDraft.trim();
    if (!name) return;
    send({ action: 'addCoOwnerByName', refrId, name });
    setCoOwnerDraft('');
  };

  // Optimistic: the server has no natural moment to push a fresh
  // housingManageState after a plain lock toggle (that flow lives in
  // FactionZoneSystem), so the label flips here and a toast confirms.
  const toggleLock = (refrId: number) => {
    send({ action: 'toggleAccess', refrId });
    patchHouse(refrId, (h) => ({ ...h, isLocked: !h.isLocked }));
  };

  const confirmRename = (refrId: number, chestRefrId: number) => {
    const name = (renameDrafts[chestRefrId] ?? '').trim();
    if (!name) return;
    send({ action: 'chestRename', refrId, chestRefrId, name });
    setRenameDrafts((prev) => {
      const next = { ...prev };
      delete next[chestRefrId];
      return next;
    });
  };

  const toggleChestMember = (refrId: number, chest: HouseChest, actorId: number) => {
    const allowed = chest.allowedActorIds.includes(actorId)
      ? chest.allowedActorIds.filter((id) => id !== actorId)
      : [...chest.allowedActorIds, actorId];
    send({ action: 'chestSetAccess', refrId, chestRefrId: chest.refrId, actorIds: allowed });
    patchHouse(refrId, (h) => ({
      ...h,
      chests: h.chests.map((c) => (c.refrId === chest.refrId ? { ...c, allowedActorIds: allowed } : c)),
    }));
  };

  const setChestRank = (refrId: number, chest: HouseChest, minRankId: number | null) => {
    send({ action: 'chestSetRankAccess', refrId, chestRefrId: chest.refrId, minRankId });
    patchHouse(refrId, (h) => ({
      ...h,
      chests: h.chests.map((c) => (c.refrId === chest.refrId ? { ...c, rankAccess: minRankId } : c)),
    }));
  };

  const offerSale = (refrId: number) => {
    const name = saleBuyer.trim();
    const price = Math.floor(Number(salePrice));
    if (!name || !Number.isFinite(price) || price < 1) return;
    send({ action: 'offerSale', refrId, name, price });
  };

  const steward = !!house && !house.isOwner;

  const tabs: Array<{ id: Tab; label: string; count?: number }> = house && house.isOwner
    ? [
        { id: 'keys', label: house.commerce ? 'Équipe' : 'Clés', count: house.members.length + (house.coOwners?.length ?? 0) },
        { id: 'chests', label: 'Coffres', count: house.chests.length },
        { id: 'journal', label: 'Journal', count: (house.journal ?? []).length },
        ...(house.canSell ? [{ id: 'sale' as Tab, label: 'Vente' }] : []),
      ]
    : [];
  const activeTab: Tab = steward ? 'chests' : tabs.some((t) => t.id === tab) ? tab : 'keys';

  return (
    <aside className="housing-drawer" role="dialog" aria-label={steward ? 'Gestion de la ville' : 'Gestion de tes logements'}>
      <header className="housing-drawer__head">
        <div>
          <h2 className="housing-drawer__title">{steward ? 'Gestion de la ville' : 'Tes logements'}</h2>
          {steward && <p className="housing-drawer__lead">Tu vois ce logement en tant que responsable de la ville. Les clés et le journal restent à l'occupant.</p>}
        </div>
        <button type="button" className="housing-btn housing-btn--quiet" onClick={close} aria-label="Fermer">
          Fermer
        </button>
      </header>

      {!house ? (
        <p className="housing-empty">Tu ne possèdes aucun logement.</p>
      ) : (
        <>
          {houses.length > 1 && (
            <div className="housing-houses" role="tablist" aria-label="Logement">
              {houses.map((h) => (
                <button
                  key={h.refrId}
                  type="button"
                  role="tab"
                  aria-selected={h.refrId === house.refrId}
                  className={'housing-houses__item' + (h.refrId === house.refrId ? ' housing-houses__item--active' : '')}
                  onClick={() => chooseHouse(h.refrId)}
                >
                  {h.displayName}
                </button>
              ))}
            </div>
          )}

          <section className="housing-house">
            <div className="housing-house__top">
              <h3 className="housing-house__name">{house.displayName}</h3>
              {house.isOwner && (
                <button
                  type="button"
                  className={'housing-btn' + (house.isLocked ? ' housing-btn--primary' : '')}
                  onClick={() => toggleLock(house.refrId)}
                >
                  {house.isLocked ? 'Déverrouiller' : 'Verrouiller'}
                </button>
              )}
            </div>
            {house.commerce && (
              <p className="housing-house__state">
                Loyer : {house.commerce.rentPricePerWeek} Septims par semaine, pris d'abord dans la caisse, puis sur ta bourse.{' '}
                {house.commerce.cashChestRefrId === null
                  ? "Aucune caisse : choisis-en une dans l'onglet Coffres."
                  : `Caisse : ${house.commerce.cashBalance ?? 0} Septims.`}
              </p>
            )}
            {house.isOwner && (
              <p className="housing-house__state">
                {house.isLocked ? 'Porte verrouillée : seuls toi et les porteurs de clés entrent.' : 'Porte ouverte : tout le monde peut entrer.'}
              </p>
            )}
          </section>

          {tabs.length > 0 && (
            <nav className="housing-tabs" role="tablist" aria-label="Sections du logement">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === t.id}
                  className={'housing-tab' + (activeTab === t.id ? ' housing-tab--active' : '')}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                  {t.count !== undefined && <span className="housing-tab__count">{t.count}</span>}
                </button>
              ))}
            </nav>
          )}

          <div className="housing-body">
            {activeTab === 'keys' && house.isOwner && house.commerce && (
              <>
                <h4 className="housing-section">
                  Copropriétaires <span className="housing-section__meta">{(house.coOwners ?? []).length}</span>
                </h4>
                <p className="housing-hint">
                  Un copropriétaire fait tout ce que tu fais : coffres, caisse, décoration, porte et employés. Il n'y a pas de limite à leur nombre. Seul le responsable du commerce les choisit.
                </p>
                {(house.coOwners ?? []).length === 0 ? (
                  <p className="housing-empty">Aucun copropriétaire.</p>
                ) : (
                  <ul className="housing-list">
                    {(house.coOwners ?? []).map((m) => (
                      <li className="housing-row" key={m.actorId}>
                        <span className="housing-row__name">{m.name}</span>
                        {house.canManageCoOwners && (
                          <button type="button" className="housing-btn housing-btn--sm housing-btn--danger" onClick={() => send({ action: 'removeCoOwner', refrId: house.refrId, actorId: m.actorId })}>
                            Retirer
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {house.canManageCoOwners && (
                  <div className="housing-form">
                    <label className="housing-label" htmlFor="housing-coowner">Ajouter un copropriétaire</label>
                    <div className="housing-form__row">
                      <input
                        id="housing-coowner"
                        className="housing-field"
                        type="text"
                        placeholder="Nom du personnage connecté"
                        value={coOwnerDraft}
                        onChange={(e) => setCoOwnerDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCoOwnerByName(house.refrId); }}
                      />
                      <button type="button" className="housing-btn housing-btn--primary" onClick={() => addCoOwnerByName(house.refrId)}>
                        Ajouter
                      </button>
                    </div>
                    <button type="button" className="housing-btn" onClick={() => send({ action: 'addCoOwnerTarget', refrId: house.refrId })}>
                      Ajouter la personne visée
                    </button>
                  </div>
                )}
              </>
            )}

            {activeTab === 'keys' && house.isOwner && (
              <>
                <h4 className="housing-section">
                  {house.commerce ? 'Employés' : 'Porteurs de clés'} <span className="housing-section__meta">{house.members.length} sur {house.maxMembers}</span>
                </h4>
                {house.commerce && (
                  <p className="housing-hint">Un employé entre dans le commerce et ouvre les coffres que tu lui ouvres, sans le gérer.</p>
                )}
                {house.members.length === 0 ? (
                  <p className="housing-empty">{house.commerce ? "Aucun employé." : "Personne n'a encore les clés."}</p>
                ) : (
                  <ul className="housing-list">
                    {house.members.map((m) => (
                      <li className="housing-row" key={m.actorId}>
                        <span className="housing-row__name">{m.name}</span>
                        <button type="button" className="housing-btn housing-btn--sm housing-btn--danger" onClick={() => send({ action: 'remove', refrId: house.refrId, actorId: m.actorId })}>
                          Retirer
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="housing-form">
                  <label className="housing-label" htmlFor="housing-name">{house.commerce ? 'Ajouter un employé' : 'Donner une clé'}</label>
                  <div className="housing-form__row">
                    <input
                      id="housing-name"
                      className="housing-field"
                      type="text"
                      placeholder="Nom du personnage connecté"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') addByName(house.refrId); }}
                    />
                    <button type="button" className="housing-btn housing-btn--primary" onClick={() => addByName(house.refrId)}>
                      Ajouter
                    </button>
                  </div>
                  <button type="button" className="housing-btn" onClick={() => send({ action: 'addTarget', refrId: house.refrId })}>
                    Ajouter la personne visée
                  </button>
                  <p className="housing-hint">Pour utiliser la personne visée, vise-la avant d'ouvrir ce panneau.</p>
                </div>
              </>
            )}

            {activeTab === 'chests' && (
              <>
                <h4 className="housing-section">
                  Coffres verrouillables <span className="housing-section__meta">{house.chests.length} sur {house.maxChests}</span>
                </h4>
                {house.chests.length === 0 ? (
                  <p className="housing-empty">Aucun coffre enregistré.</p>
                ) : (
                  <ul className="housing-list">
                    {house.chests.map((chest) => {
                      const renaming = renameDrafts[chest.refrId] !== undefined;
                      const expanded = expandedChest === chest.refrId;
                      return (
                        <li className="housing-chest" key={chest.refrId}>
                          <div className="housing-row">
                            {renaming ? (
                              <input
                                className="housing-field housing-field--inline"
                                type="text"
                                autoFocus
                                aria-label="Nouveau nom du coffre"
                                value={renameDrafts[chest.refrId]}
                                onChange={(e) => setRenameDrafts((prev) => ({ ...prev, [chest.refrId]: e.target.value }))}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') confirmRename(house.refrId, chest.refrId);
                                  if (e.key === 'Escape') setRenameDrafts((prev) => { const n = { ...prev }; delete n[chest.refrId]; return n; });
                                }}
                              />
                            ) : (
                              <span className="housing-row__name" title={`id ${chest.id}`}>
                                {chest.name}
                                {chest.isCash && <span className="housing-section__meta"> caisse</span>}
                              </span>
                            )}
                            <span className="housing-row__actions">
                              {renaming ? (
                                <>
                                  <button type="button" className="housing-btn housing-btn--sm housing-btn--primary" onClick={() => confirmRename(house.refrId, chest.refrId)}>Valider</button>
                                  <button type="button" className="housing-btn housing-btn--sm" onClick={() => setRenameDrafts((prev) => { const n = { ...prev }; delete n[chest.refrId]; return n; })}>Annuler</button>
                                </>
                              ) : (
                                <>
                                  <button type="button" className="housing-btn housing-btn--sm" onClick={() => setRenameDrafts((prev) => ({ ...prev, [chest.refrId]: chest.name }))}>Renommer</button>
                                  {house.commerce && house.isOwner && (
                                    <button
                                      type="button"
                                      className={'housing-btn housing-btn--sm' + (chest.isCash ? ' housing-btn--primary' : '')}
                                      onClick={() => send({ action: 'setCashChest', refrId: house.refrId, chestRefrId: chest.isCash ? null : chest.refrId })}
                                    >
                                      {chest.isCash ? 'Plus la caisse' : 'Définir comme caisse'}
                                    </button>
                                  )}
                                  <button type="button" className="housing-btn housing-btn--sm" aria-expanded={expanded} onClick={() => setExpandedChest(expanded ? null : chest.refrId)}>Accès</button>
                                  <button type="button" className="housing-btn housing-btn--sm housing-btn--danger" onClick={() => {
                                    send({ action: 'chestRemove', refrId: house.refrId, chestRefrId: chest.refrId });
                                    if (expandedChest === chest.refrId) setExpandedChest(null);
                                  }}>Retirer</button>
                                </>
                              )}
                            </span>
                          </div>

                          {expanded && (
                            <div className="housing-access">
                              {(house.holdRanks ?? []).length > 0 && (
                                <div className="housing-form">
                                  <label className="housing-label" htmlFor={`housing-rank-${chest.refrId}`}>Ouvert au rang de la ville</label>
                                  <select
                                    id={`housing-rank-${chest.refrId}`}
                                    className="housing-field"
                                    value={chest.rankAccess ?? ''}
                                    onChange={(e) => setChestRank(house.refrId, chest, e.target.value === '' ? null : Number(e.target.value))}
                                  >
                                    <option value="">Personne, seulement les clés cochées</option>
                                    {(house.holdRanks ?? []).map((r) => (
                                      <option key={r.id} value={r.id}>{r.name} et au-dessus</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              <p className="housing-hint">
                                {steward ? "L'occupant a toujours accès. Coche qui d'autre peut ouvrir ce coffre :" : "Tu as toujours accès. Coche qui d'autre peut ouvrir ce coffre :"}
                              </p>
                              {house.members.length === 0 ? (
                                <p className="housing-empty">Aucun porteur de clés à qui donner accès.</p>
                              ) : (
                                house.members.map((m) => (
                                  <label className="housing-check" key={m.actorId}>
                                    <input
                                      type="checkbox"
                                      checked={chest.allowedActorIds.includes(m.actorId)}
                                      onChange={() => toggleChestMember(house.refrId, chest, m.actorId)}
                                    />
                                    {m.name}
                                  </label>
                                ))
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {house.chests.length < house.maxChests && (
                  <p className="housing-hint">
                    Pour enregistrer un coffre : vise-le à l'intérieur du logement, fais ALT et choisis Enregistrer ce coffre.
                  </p>
                )}
              </>
            )}

            {activeTab === 'journal' && house.isOwner && (
              <>
                <h4 className="housing-section">Journal du logement</h4>
                {(house.journal ?? []).length === 0 ? (
                  <p className="housing-empty">Rien à signaler.</p>
                ) : (
                  <ul className="housing-journal">
                    {(house.journal ?? []).map((e, i) => (
                      <li className="housing-journal__row" key={i}>
                        <time className="housing-journal__date">{formatJournalDate(e.at)}</time>
                        <span>{e.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {activeTab === 'sale' && house.isOwner && house.canSell && (
              <>
                <h4 className="housing-section">Vendre ce logement</h4>
                <div className="housing-form">
                  <label className="housing-label" htmlFor="housing-buyer">Acheteur</label>
                  <input
                    id="housing-buyer"
                    className="housing-field"
                    type="text"
                    placeholder="Nom du personnage connecté"
                    value={saleBuyer}
                    onChange={(e) => setSaleBuyer(e.target.value)}
                  />
                  <label className="housing-label" htmlFor="housing-price">Prix en Septims</label>
                  <input
                    id="housing-price"
                    className="housing-field"
                    type="number"
                    min={1}
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                  />
                  <div className="housing-form__actions">
                    <button type="button" className="housing-btn housing-btn--primary" onClick={() => offerSale(house.refrId)}>
                      Proposer la vente
                    </button>
                  </div>
                  <p className="housing-hint">L'acheteur répond avec /accepterlogement ou /refuserlogement. Les clés et les coffres ne sont pas transférés.</p>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </aside>
  );
};

export default HousingManage;
